import { useToast, UseToastOptions, ToastId } from '@chakra-ui/react';

export interface ToastHelperOptions {
  title?: string;
  description?: string;
  duration?: number;
  isClosable?: boolean;
  position?: UseToastOptions['position'];
  id?: string;
}

export const useToastHelpers = () => {
  const toast = useToast();

  const defaultOptions: Partial<UseToastOptions> = {
    duration: 5000,
    isClosable: true,
    position: 'bottom-right',
  };

  const showSuccessToast = (options: ToastHelperOptions): ToastId => {
    options.title ??= 'Operación exitosa';
    return toast({
      ...defaultOptions,
      ...options,
      status: 'success',
      id: options.id || `success-toast-${Date.now()}`,
    });
  };

  const showErrorToast = (options: ToastHelperOptions): ToastId => {
    options.title ??= 'Error';
    return toast({
      ...defaultOptions,
      ...options,
      status: 'error',
      id: options.id || `error-toast-${Date.now()}`,
    });
  };

  const showWarningToast = (options: ToastHelperOptions): ToastId => {
    return toast({
      ...defaultOptions,
      ...options,
      status: 'warning',
      id: options.id || `warning-toast-${Date.now()}`,
    });
  };

  const showInfoToast = (options: ToastHelperOptions): ToastId => {
    return toast({
      ...defaultOptions,
      ...options,
      status: 'info',
      id: options.id || `info-toast-${Date.now()}`,
    });
  };

  const showLoadingToast = (options: ToastHelperOptions): ToastId => {
    return toast({
      ...defaultOptions,
      ...options,
      status: 'loading',
      duration: null, // Loading toasts don't auto-dismiss
      id: options.id || `loading-toast-${Date.now()}`,
    });
  };

  const isToastActive = (id: string): boolean => {
    return toast.isActive(id);
  };

  const closeToast = (id: string): void => {
    toast.close(id);
  };

  const closeAllToasts = (): void => {
    toast.closeAll();
  };

  return {
    showSuccessToast,
    showErrorToast,
    showWarningToast,
    showInfoToast,
    showLoadingToast,
    isToastActive,
    closeToast,
    closeAllToasts,
  };
};
